import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { teamKeys } from '../queries/keys';
import { teamPaths } from '../queries/paths';

// TSS vite/03 — server state for the `teams` entity and its membership.
//
// Membership is a second table with rules of its own, so it gets its own
// queries and its own cache key rather than riding inside the team.

export const TEAMS_PAGE_SIZE = 25;   // the backend default; hard cap is 100

// A QUÉ EQUIPOS PERTENECE UNA PERSONA NO SE ARMA ACÁ, y eso es nuevo.
//
// This file used to hold a `useTeamMemberships()` that read every team, then
// every team's members, and turned the result inside out — because there was no
// route for it. AD01 published `GET /admin/users/{id}/teams`, so the inversion
// is gone and the question is answered where it belongs, in
// `features/users/contexts/users.jsx`.

// GET /api/v1/admin/teams -> { items, pagination: { total, limit, offset } }
//
// `q` searches the name. And each team carries `members_count` (tier 12,
// T12.3), so the cards no longer ask every team for its people — that was one
// request per card and it is gone.
export function useTeamsList({ limit = TEAMS_PAGE_SIZE, offset = 0, q } = {}) {
    const params = { limit, offset };
    if (q) params.q = q;

    return useQuery({
        queryKey: teamKeys.list(params),
        queryFn: ({ signal }) => api
            .get(teamPaths.list(), { params, signal })
            .then(r => r.data),
        placeholderData: keepPreviousData,
    });
}

export function useTeam(id) {
    return useQuery({
        queryKey: teamKeys.detail(id),
        queryFn: ({ signal }) => api.get(teamPaths.detail(id), { signal }).then(r => r.data),
        enabled: !!id,
    });
}

// GET /admin/teams/{id}/members -> { team, members: [{ id, user_id, team_role,
// status, user }] }. Only the active ones: somebody removed from the team keeps
// their row with status 1 and does not come back here.
export function useTeamMembers(id) {
    return useQuery({
        queryKey: teamKeys.members(id),
        queryFn: ({ signal }) => api.get(teamPaths.members(id), { signal }).then(r => r.data),
        enabled: !!id,
    });
}

export function useCreateTeam() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(teamPaths.list(), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.all }),
    });
}

export function useUpdateTeam(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(teamPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: teamKeys.detail(id) });
            qc.invalidateQueries({ queryKey: teamKeys.all });
        },
    });
}

// POST /admin/teams/{id}/members — { user_id, team_role }.
// Answers 409 if that person is already in the team, and reactivates the
// membership if they had been removed.
export function useAddTeamMember(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(teamPaths.members(id), payload).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.members(id) }),
    });
}

// DELETE /admin/teams/{id}/members/{userId} — soft removal: the membership
// goes to status 1 and stops granting scope. The row survives, because who was
// in the team when a task was claimed is part of the record.
//
// It takes the USER's id and not the membership id — the URL says so.
export function useRemoveTeamMember(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (userId) => api.delete(teamPaths.member(id, userId)).then(r => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.members(id) }),
    });
}

// The picker's fetcher. It lives here because the endpoint does: a form asking
// for options must not know the URL.
//
// It takes the search term and NOT a page: a picker's job is to find one
// record, and past twenty results the answer is to type more, not to paginate.
export function searchTeams(q, signal) {
    const params = { limit: 20, offset: 0 };
    if (q) params.q = q;
    return api
        .get(teamPaths.list(), { params, signal })
        .then(r => r.data.items ?? []);
}
